import React, { useRef, useEffect, useCallback } from 'react';
import cn from 'classnames';
import Spinner from '@creditflowresearch/uilib/Loader/Spinner';
import SearchResults from './SearchResults';
import { isEscapeKey } from '@framework/browser/checkPressedKey';
import { HEADER_SEARCH_INPUT_ID } from '@/creditflowresearch/application/SearchHeaderMenu/constants';
import { useHeaderMenuContext } from '@/creditflowresearch/application/SearchHeaderMenu/HeaderMenuContext';
import useHeaderMenuData from '@/creditflowresearch/application/HeaderMenu/useHeaderMenuData';
import MissingSomethingModalProvider, { useMissingSomethingModalContext } from '@/creditflowresearch/application/MissingSomethingModalContext';
import MissingSomethingModalForm from '@/creditflowresearch/ui/common/components/MissingSomethingModalForm';

import styles from './searchResultsMenu.module.scss';
import headerStyles from '@/creditflowresearch/ui/common/components/header/Header/header.module.scss';

interface IProps {
  isSearchFetching: boolean,
  variant?: 'default' | 'creditflow',
}

const SearchResultsMenuInner = ({
  isSearchFetching,
  variant = 'default',
}: IProps) => {
  const searchResultsRef = useRef(null);
  const { isHeaderInitialized } = useHeaderMenuData();
  const { isVisible: isMissingSomethingModalVisible } = useMissingSomethingModalContext();
  const {
    isSearchResultsOpen,
    setIsSearchResultsOpen,
  } = useHeaderMenuContext();

  const searchResultHandlerEsc = useCallback((event) => {
    const isEscape = isEscapeKey(event);

    if (!isMissingSomethingModalVisible) {
      if (isEscape && isSearchResultsOpen) {
        // @ts-ignore
        setIsSearchResultsOpen(false);
      }
    }
  }, [isMissingSomethingModalVisible, isSearchResultsOpen]);

  const searchResultHandlerOutsideClick = useCallback((ev) => {
    // @ts-ignore
    if (!!ev.target && searchResultsRef.current && !searchResultsRef.current.contains(ev.target)) {
      if (
        isSearchResultsOpen
        && (ev.target.id !== HEADER_SEARCH_INPUT_ID)
        && (ev.target.offsetParent?.id !== HEADER_SEARCH_INPUT_ID)
      ) {
        // @ts-ignore
        setIsSearchResultsOpen(false);
      }
    }
  }, [searchResultsRef.current, isSearchResultsOpen]);

  useEffect(() => {
    if (isSearchResultsOpen && !isMissingSomethingModalVisible) {
      document.addEventListener('click', searchResultHandlerOutsideClick);
      document.addEventListener('keydown', searchResultHandlerEsc);
    }

    return () => {
      document.removeEventListener('click', searchResultHandlerOutsideClick);
      document.removeEventListener('keydown', searchResultHandlerEsc);
    };
  }, [
    isSearchResultsOpen,
    searchResultHandlerOutsideClick,
    searchResultHandlerEsc,
  ]);

  useEffect(() => {
    if (isSearchFetching) {
      // @ts-ignore
      searchResultsRef.current.scrollTop = 0;
    }
  }, [isSearchFetching]);

  return (
    <div
      ref={ searchResultsRef }
      className={
        cn(styles.container, {
          [styles.isSearchFetching]: isSearchFetching,
          [styles.isSearchResultsOpen]: isSearchResultsOpen,
          [styles.creditflowVariant]: variant === 'creditflow',
        })
      }
    >
      <Spinner
        overlay
        className={ headerStyles.spinner }
        overlayClassName={ cn(headerStyles.spinnerOverlay, { [headerStyles.cfrVariant]: variant === 'creditflow' }) }
        isVisible={ !isHeaderInitialized || isSearchFetching }
      />
      { isHeaderInitialized && <SearchResults variant={ variant } /> }
    </div>
  );
};

const SearchResultsMenu = (props: IProps) => (
  <MissingSomethingModalProvider>
    <SearchResultsMenuInner { ...props } />
    <MissingSomethingModalForm />
  </MissingSomethingModalProvider>
);

export default SearchResultsMenu;
